import LoadingOverlay from '@ri-digital/web-component-library/src/molecule/LoadingOverlay';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import EuDutyModal from '../../components/EuDutyModal';
import USRedirectModal from '../../components/USRedirectModal/USRedirectModal';
import { setFaroIsGuestAttribute } from '../../helpers/faroLogger';
import { useFeaturesConfig } from '../../hooks/useFeaturesConfig';
import { useInitApp } from '../../hooks/useInitApp';
import { useRedirectToCart } from '../../hooks/useRedirectToCart';
import useUniversalVariable from '../../hooks/useUniversalVariable';
import { useUserProperties } from '../../hooks/useUserProperties';
import {
  featuresConfig as featuresConfigVar,
  redirectToGlobalE,
  userProps,
} from '../../lib/reactives';
import GlobalERoute from '../../routes/GlobalE';
import MainRoutes from '../../routes/MainRoutes';
import { Routes } from '../../routes/routes';

const MainLayout = ({ hasPaymentIntent } = {}) => {
  const { pathname } = window.location;
  const [searchParams] = useSearchParams();

  const { cart, loading: cartLoading, error: cartError } = useInitApp();

  const {
    userProperties,
    loading: userPropertiesLoading,
    error: userPropertiesError,
  } = useUserProperties({
    skip: cart?.isGuest || cartLoading || hasPaymentIntent,
  });

  const { loading: featuresConfigLoading, error: featuresConfigError } =
    useFeaturesConfig(cart?.locale?.country, {
      skip: !cart?.locale?.country,
      onCompleted: ({ featuresConfig }) => featuresConfigVar(featuresConfig),
    });

  const isLoading =
    cartLoading || featuresConfigLoading || userPropertiesLoading;
  const isError = cartError || featuresConfigError || userPropertiesError;

  // Tracks whether the app has completed its initial load at least once.
  // EuDutyModal only mounts after that point (see render below) so its own
  // queries never race the initial cart/auth bootstrap - but once mounted,
  // it stays mounted even if isLoading flips true again later (e.g. a
  // currency conversion changing cart.locale.country re-triggers the
  // featuresConfig fetch above), so it doesn't lose its open state.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setHasLoadedOnce(true);
    }
  }, [isLoading]);
  const isGlobalECheckout =
    Routes.localCheckout.path === pathname && searchParams.get('token');

  const redirectGlobalE = redirectToGlobalE();

  useRedirectToCart(cart?.lineItems, isGlobalECheckout);

  useEffect(() => {
    const props = {
      ...(userProperties || {}),
      ...(cart ? { ...cart?.locale, shippingPass: cart?.shippingPass } : {}),
      ...(cart?.isGuest
        ? {
            isGuest: true,
          }
        : {}),
    };

    if (Object.keys(props)) {
      userProps(props);
    }

    if (cart) {
      // Set isGuest as a global context attribute in Faro
      setFaroIsGuestAttribute(!!cart?.isGuest);
    }
  }, [userProperties, cart]);

  useUniversalVariable(cart, userProperties);

  const getCheckoutContent = () => {
    if (
      isGlobalECheckout ||
      cart?.checkoutFlow === 'globale' ||
      redirectGlobalE
    ) {
      return (
        <GlobalERoute analytics={Routes.localCheckout.analytics} cart={cart} />
      );
    }

    return (
      <>
        <USRedirectModal />
        <MainRoutes cart={cart} error={isError} loading={isLoading} />
      </>
    );
  };

  const renderRouteContent = () => {
    if (isLoading) {
      return <LoadingOverlay loading variant="inOverlay" />;
    }

    return hasPaymentIntent ||
      cart?.lineItems.length > 0 ||
      isGlobalECheckout ? (
      getCheckoutContent()
    ) : (
      <LoadingOverlay loading variant="inOverlay" />
    );
  };

  return (
    <>
      {hasLoadedOnce && <EuDutyModal />}
      {renderRouteContent()}
    </>
  );
};

MainLayout.propTypes = {
  hasPaymentIntent: PropTypes.bool,
};

MainLayout.defaultProps = {
  hasPaymentIntent: false,
};

export default MainLayout;
